import React from 'react';
import { ContentfulContactBlock, ContentfulEntry } from '@jbhi-fi/apis/src/contentful/types';
import { ListingBlock } from '../ListingBlock';
import { ArticleBlockTransformer } from '../Contentful/transformers/ArticleBlockTransformer';
import { ListingBlockTransformer } from '../Contentful/transformers/ListingBlockTransformer';
import { ContactBlockTransformer } from '../Contentful/transformers/ContactBlockTransformer';
import { CarouselTransformer } from '../Contentful/transformers/CarouselTransformer';
import { TabsBlock } from '../TabsBlock/TabsBlock';
import { PromoCard } from '../PromoCard/PromoCard';
import { PromoCardTransformer } from '../Contentful/transformers/PromoCardTransformer';
import { ProductBlockTransformer } from '../Contentful/transformers/ProductBlockTransformer';
import { HeroBanner } from '../HeroBanner/HeroBanner';
import { LeaderboardBannerTransformer } from '../Contentful/transformers/LeaderboardBannerTransformer';
import { ContentfulPagesList } from '../ContentfulPagesList/ContentfulPagesList';
import { PageListingTransformer } from '../Contentful/transformers/PageListingTransformer';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import { ButtonTransformer } from '../Contentful/transformers/ButtonTransformer';
import { ButtonBlock } from '../ButtonBlock/ButtonBlock';
import { ButtonBlockTransformer } from '../Contentful/transformers/ButtonBlockTransformer';
import { ContentAccordion } from '../ContentAccordion/ContentAccordion';
import { AccordionTransformer } from '../Contentful/transformers/AccordionTransformer';
import { MultiColumnBlock } from '../MultiColumnBlock/MultiColumnBlock';
import { MultiColumnBlockTransformer } from '../Contentful/transformers/MultiColumnBlockTransformer';
import { ContentfulImage } from '../ContentfulImage/ContentfulImage';
import { AssetTransformer } from '../Contentful/transformers/AssetTransformer';
import { determineCalloutTransformer } from '../Contentful/transformers/determineCalloutTransformer';
import { SideBySide } from '../SideBySide/SideBySide';
import { SideBySideTransformer } from '../Contentful/transformers/SideBySideTransformer';
import { ContentBanner } from '../ContentBanner/ContentBanner';
import { ContentBannerTransformer } from '../Contentful/transformers/ContentBannerTransformer';
import { ContactBlock } from '../ContactBlock';
import { LegacyContactBlock } from '../ContactBlock/LegacyContactBlock/LegacyContactBlock';
import { getSalesLineHours } from '@jbhi-fi/components/src/ContactBlock/utils/salesLine/salesLine';
import { GoToLink } from '../GoToLink/GoToLink';
import { CompetitionGridTransformer } from '../Contentful/transformers/CompetitionGridTransformer';
import { CompetitionStatusGrid } from '../CompetitionStatusGrid/CompetitionStatusGrid';
import { ScrollTarget } from '../ScrollTarget/ScrollTarget';
import { AnchorTransformer } from '../Contentful/transformers/AnchorTransformer';
import { TableOfContents } from '../TableOfContents/TableOfContents';
import { TableOfContentsTransformer } from '../Contentful/transformers/TableOfContentsTransformer';
import { PreezieFinder } from '../PreezieFinder/PreezieFinder';
import { PreezieBannerTransformer } from '../Contentful/transformers/PreezieBannerTransformer';

export type ContentEntry = {
  Component: React.VFC<any>;
  transformer: (entry: ContentfulEntry<any>) => Record<string, any> | undefined;
};

type ContentEntryKey =
  | 'articleBlock'
  | 'Asset'
  | 'button'
  | 'buttonBlock'
  | 'carousel'
  | 'contactBlock'
  | 'leaderboardBanner'
  | 'listingBlock'
  | 'pageListing'
  | 'productBlock'
  | 'promoCard'
  | 'accordion'
  | 'multiColumnBlock'
  | 'callout'
  | 'sideBySide'
  | 'contentBanner'
  | 'competitionGrid'
  | 'anchor'
  | 'tableOfContents'
  | 'preezieBanner';

const mapEntryToKey = (entry: ContentfulEntry<unknown>): ContentEntryKey | undefined => {
  if (entry?.sys?.contentType?.sys?.id) {
    return entry.sys.contentType.sys.id as ContentEntryKey;
  }
  if (entry?.sys?.type === 'Asset') {
    return entry.sys.type as ContentEntryKey;
  }
  return undefined;
};

export const getContentEntry = (
  entry: ContentfulEntry<unknown>,
  overrides: Partial<typeof CONTENT_ENTRIES> = {}
): ContentEntry | undefined => {
  const entryKey = mapEntryToKey(entry);
  const contentEntry = entryKey ? { ...CONTENT_ENTRIES, ...overrides }[entryKey] : undefined;
  return typeof contentEntry === 'function' ? contentEntry(entry) : contentEntry;
};

export const CONTENT_ENTRIES: Record<ContentEntryKey, ContentEntry | ((entry: ContentfulEntry<any>) => ContentEntry)> =
  {
    articleBlock: {
      Component: TabsBlock,
      transformer: ArticleBlockTransformer.toTabsBlockProps,
    },
    Asset: {
      Component: ContentfulImage,
      transformer: AssetTransformer.toContentfulImagePropsWithOriginalWidth,
    },
    button: (entry: ContentfulEntry<any>) => {
      const isLinkWithoutIcon = entry?.fields?.type === 'Link' && !entry?.fields?.icon;
      return {
        Component: isLinkWithoutIcon ? GoToLink : Button,
        transformer: isLinkWithoutIcon ? ButtonTransformer.toGoToLinkProps : ButtonTransformer.toLanyardButtonProps,
      };
    },
    buttonBlock: {
      Component: ButtonBlock,
      transformer: ButtonBlockTransformer.toButtonBlockProps,
    },
    carousel: CarouselTransformer.toContentEntryProps,
    contactBlock: {
      Component: window.Jbhifi?.storeLocation === 'AU' ? ContactBlock : LegacyContactBlock,
      transformer: (e: ContentfulContactBlock) =>
        ContactBlockTransformer.toContactBlockProps(e, {
          salesLineHours: getSalesLineHours(),
        }),
    },
    leaderboardBanner: {
      Component: HeroBanner,
      transformer: LeaderboardBannerTransformer.toHeroBannerProps,
    },
    listingBlock: {
      Component: ListingBlock,
      transformer: ListingBlockTransformer.toListingBlockProps,
    },
    pageListing: {
      Component: ContentfulPagesList,
      transformer: PageListingTransformer.toContentfulPagesListProps,
    },
    productBlock: {
      Component: TabsBlock,
      transformer: ProductBlockTransformer.toTabsBlockProps,
    },
    promoCard: {
      Component: PromoCard,
      transformer: PromoCardTransformer.toPromoCardProps,
    },
    accordion: {
      Component: ContentAccordion,
      transformer: AccordionTransformer.toAccordionProps,
    },
    multiColumnBlock: {
      Component: MultiColumnBlock,
      transformer: MultiColumnBlockTransformer.toMultiColumnBlockProps,
    },
    callout: entry => {
      const result = determineCalloutTransformer(entry);
      return {
        Component: result.OutputComponent,
        transformer: result.transformer,
      };
    },
    sideBySide: () => {
      return {
        Component: SideBySide,
        transformer: SideBySideTransformer.toSideBySideProps,
      };
    },
    contentBanner: {
      Component: ContentBanner,
      transformer: ContentBannerTransformer.toContentBannerProps,
    },
    competitionGrid: {
      Component: CompetitionStatusGrid,
      transformer: CompetitionGridTransformer.toCompetitionStatusGridProps,
    },
    anchor: {
      Component: ScrollTarget,
      transformer: entry => ({
        ...AnchorTransformer.toScrollTargetProps(entry),
        headerOffset: 80,
      }),
    },
    tableOfContents: {
      Component: TableOfContents,
      transformer: TableOfContentsTransformer.toTableOfContentsProps,
    },
    preezieBanner: {
      Component: PreezieFinder,
      transformer: PreezieBannerTransformer.toPreezieFinderProps,
    },
  };
